export const UNICORN_CAUSES = [
  'stale-build',
  'auth',
  'data-load',
  'network',
  'unknown',
] as const;

export type UnicornCause = typeof UNICORN_CAUSES[number];

export const UNICORN_PRODUCERS = [
  'global-error-handler',
  'patients-resolver',
  'unknown',
] as const;

export type UnicornProducer = typeof UNICORN_PRODUCERS[number];

export const UNICORN_STALENESS_VERDICTS = [
  'fresh',
  'stale',
  'unavailable',
  'unresolved',
] as const;

export type UnicornStalenessVerdict =
  typeof UNICORN_STALENESS_VERDICTS[number];

export const UNICORN_SERVICE_WORKER_STATES = [
  'active',
  'waiting',
  'installing',
  'unsupported',
  'disabled',
  'unresolved',
] as const;

export type UnicornServiceWorkerState =
  typeof UNICORN_SERVICE_WORKER_STATES[number];

export const UNICORN_CONNECTIVITY_STATES = [
  'online',
  'offline',
  'unresolved',
] as const;

export type UnicornConnectivityState =
  typeof UNICORN_CONNECTIVITY_STATES[number];

export const UNICORN_RECOVERY_RUNGS = ['primary', 'fallback'] as const;
export type UnicornRecoveryRung = typeof UNICORN_RECOVERY_RUNGS[number];

export const UNICORN_RECOVERY_ACTIONS = [
  'hard-refresh',
  'plain-refresh',
  'interactive-reauth',
  'reset-auth-session',
  'network-retry',
  'public-search',
] as const;
export type UnicornRecoveryAction = typeof UNICORN_RECOVERY_ACTIONS[number];

export const UNICORN_RECOVERY_OUTCOMES = [
  'success',
  'failure',
  'expired',
  'unmeasurable',
] as const;
export type UnicornRecoveryOutcome = typeof UNICORN_RECOVERY_OUTCOMES[number];

export const UNICORN_CLEANUP_RESULTS = [
  'released',
  'no-op',
  'failed',
  'unresolved',
] as const;
export type UnicornCleanupResult = typeof UNICORN_CLEANUP_RESULTS[number];

export const UNICORN_CAUSE_STATE_KEY = 'unicornCause';
export const UNICORN_PRODUCER_STATE_KEY = 'unicornProducer';
export const UNICORN_UNRESOLVED_BUILD_IDENTITY = 'unresolved';
export const UNICORN_BUILD_IDENTITY_PLACEHOLDER = '__RETTX_BUILD_ID__';
export const UNICORN_BUILD_IDENTITY_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;
export const UNICORN_INCIDENT_CODE_PATTERN =
  /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
// FR-010/R1 loop guard: five minutes bounds caregiver dead time between attempts.
export const UNICORN_RECOVERY_GUARD_TTL_MS = 5 * 60 * 1000;
// SC-6 measurement horizon: after ten minutes the recovery truthfully expired.
export const UNICORN_RECOVERY_PENDING_TTL_MS = 10 * 60 * 1000;
export const UNICORN_RECOVERY_PENDING_KEY = 'rettx.unicorn.recovery.pending';
export const UNICORN_RECOVERY_GUARD_PREFIX = 'rettx.unicorn.recovery.guard';

let fallbackIncidentSequence = 0;

export interface UnicornShownProperties {
  cause: UnicornCause;
  producer: UnicornProducer;
  build_identity: string;
  staleness: UnicornStalenessVerdict;
  service_worker: UnicornServiceWorkerState;
  connectivity: UnicornConnectivityState;
  incident_code: string;
}

export interface UnicornRecoveryAttemptProperties
  extends Record<string, string | number> {
  incident_code: string;
  cause: UnicornCause;
  action: UnicornRecoveryAction;
  rung: UnicornRecoveryRung;
}

export interface UnicornRecoveryOutcomeProperties
  extends UnicornRecoveryAttemptProperties, Record<string, string | number> {
  outcome: UnicornRecoveryOutcome;
  cleanup: UnicornCleanupResult;
  duration_ms: number;
  destination: string;
}

export interface UnicornPendingRecovery {
  incidentCode: string;
  cause: UnicornCause;
  action: UnicornRecoveryAction;
  rung: UnicornRecoveryRung;
  cleanup: UnicornCleanupResult;
  startedAt: number;
  unicornShownAt: number;
  destination: string;
}

export function isUnicornCause(value: unknown): value is UnicornCause {
  return UNICORN_CAUSES.some(cause => cause === value);
}

export function isUnicornProducer(value: unknown): value is UnicornProducer {
  return UNICORN_PRODUCERS.some(producer => producer === value);
}

export function normalizeUnicornBuildIdentity(value: unknown): string {
  if (
    typeof value !== 'string' ||
    value === UNICORN_BUILD_IDENTITY_PLACEHOLDER ||
    !UNICORN_BUILD_IDENTITY_PATTERN.test(value)
  ) {
    return UNICORN_UNRESOLVED_BUILD_IDENTITY;
  }
  return value;
}

export function isUnicornStalenessVerdict(
  value: unknown
): value is UnicornStalenessVerdict {
  return UNICORN_STALENESS_VERDICTS.some(verdict => verdict === value);
}

export function isUnicornServiceWorkerState(
  value: unknown
): value is UnicornServiceWorkerState {
  return UNICORN_SERVICE_WORKER_STATES.some(state => state === value);
}

export function isUnicornConnectivityState(
  value: unknown
): value is UnicornConnectivityState {
  return UNICORN_CONNECTIVITY_STATES.some(state => state === value);
}

export function isUnicornRecoveryRung(
  value: unknown
): value is UnicornRecoveryRung {
  return UNICORN_RECOVERY_RUNGS.some(rung => rung === value);
}

export function isUnicornRecoveryAction(
  value: unknown
): value is UnicornRecoveryAction {
  return UNICORN_RECOVERY_ACTIONS.some(action => action === value);
}

export function isUnicornCleanupResult(
  value: unknown
): value is UnicornCleanupResult {
  return UNICORN_CLEANUP_RESULTS.some(result => result === value);
}

export function normalizeUnicornIncidentCode(value: unknown): string {
  return typeof value === 'string' && UNICORN_INCIDENT_CODE_PATTERN.test(value)
    ? value
    : 'SAFE-2222';
}

export function createUnicornIncidentCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const indexes = new Uint8Array(8);
  try {
    globalThis.crypto.getRandomValues(indexes);
    return formatIncidentCode(
      Array.from(indexes, value => alphabet[value % alphabet.length])
    );
  } catch {
    try {
      return formatIncidentCode(
        Array.from({ length: 8 }, () => {
          const index = Math.floor(Math.random() * alphabet.length);
          return alphabet[index] ?? alphabet[0];
        })
      );
    } catch {
      fallbackIncidentSequence += 1;
      let seed: number;
      try {
        seed = Date.now() + fallbackIncidentSequence;
      } catch {
        seed = fallbackIncidentSequence;
      }
      return formatIncidentCode(
        Array.from({ length: 8 }, () => {
          const index = Math.abs(seed % alphabet.length);
          seed = Math.floor(seed / alphabet.length) + fallbackIncidentSequence;
          return alphabet[index] ?? alphabet[0];
        })
      );
    }
  }
}

export function normalizeUnicornRoute(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return '/';
  }
  const firstSegment = value.split(/[?#]/, 1)[0]
    .split('/')
    .filter(Boolean)[0];
  return firstSegment && /^[a-z0-9-]+$/i.test(firstSegment)
    ? `/${firstSegment}`
    : '/';
}

export function createUnicornRecoveryAttemptProperties(
  incidentCode: unknown,
  cause: unknown,
  action: unknown,
  rung: unknown,
): UnicornRecoveryAttemptProperties {
  return {
    incident_code: normalizeUnicornIncidentCode(incidentCode),
    cause: isUnicornCause(cause) ? cause : 'unknown',
    action: isUnicornRecoveryAction(action) ? action : 'plain-refresh',
    rung: isUnicornRecoveryRung(rung) ? rung : 'fallback',
  };
}

export function createUnicornRecoveryOutcomeProperties(
  pending: UnicornPendingRecovery,
  outcome: UnicornRecoveryOutcome,
  now: number,
): UnicornRecoveryOutcomeProperties {
  return {
    ...createUnicornRecoveryAttemptProperties(
      pending.incidentCode,
      pending.cause,
      pending.action,
      pending.rung,
    ),
    outcome,
    cleanup: isUnicornCleanupResult(pending.cleanup)
      ? pending.cleanup
      : 'unresolved',
    duration_ms: normalizeDuration(now - pending.unicornShownAt),
    destination: normalizeUnicornRoute(pending.destination),
  };
}

export function parseUnicornPendingRecovery(
  value: unknown
): UnicornPendingRecovery | undefined {
  let candidate: unknown = value;
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (typeof candidate !== 'object' || candidate === null) {
    return undefined;
  }
  const record = candidate as Record<string, unknown>;
  if (
    !UNICORN_INCIDENT_CODE_PATTERN.test(String(record['incidentCode'] ?? '')) ||
    !isUnicornCause(record['cause']) ||
    !isUnicornRecoveryAction(record['action']) ||
    !isUnicornRecoveryRung(record['rung']) ||
    !isUnicornCleanupResult(record['cleanup']) ||
    !isSafeTimestamp(record['startedAt']) ||
    !isSafeTimestamp(record['unicornShownAt']) ||
    typeof record['destination'] !== 'string'
  ) {
    return undefined;
  }
  return {
    incidentCode: String(record['incidentCode']),
    cause: record['cause'],
    action: record['action'],
    rung: record['rung'],
    cleanup: record['cleanup'],
    startedAt: record['startedAt'],
    unicornShownAt: record['unicornShownAt'],
    destination: normalizeUnicornRoute(record['destination']),
  };
}

export function getUnicornRecoveryGuardKey(
  incidentCode: string,
  rung: UnicornRecoveryRung,
): string {
  return `${UNICORN_RECOVERY_GUARD_PREFIX}.${normalizeUnicornIncidentCode(incidentCode)}.${rung}`;
}

export function createUnicornShownProperties(
  cause: unknown,
  producer: unknown,
  buildIdentity: unknown,
  staleness: unknown,
  serviceWorker: unknown,
  connectivity: unknown,
  incidentCode: unknown,
): UnicornShownProperties {
  return {
    cause: isUnicornCause(cause) ? cause : 'unknown',
    producer: isUnicornProducer(producer) ? producer : 'unknown',
    build_identity: normalizeUnicornBuildIdentity(buildIdentity),
    staleness: isUnicornStalenessVerdict(staleness)
      ? staleness
      : 'unresolved',
    service_worker: isUnicornServiceWorkerState(serviceWorker)
      ? serviceWorker
      : 'unresolved',
    connectivity: isUnicornConnectivityState(connectivity)
      ? connectivity
      : 'unresolved',
    incident_code: normalizeUnicornIncidentCode(incidentCode),
  };
}

function formatIncidentCode(characters: string[]): string {
  return `${characters.slice(0, 4).join('')}-${characters.slice(4).join('')}`;
}

function isSafeTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function normalizeDuration(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}
